/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import {
    type BenefitType,
    type NetworkStatus,
    type ReferentPriceType,
    type USState,
} from '@strata/constants';
import { DateFormats, formatDate, formatNameLastFirst, sortAsc } from '@strata/helper-functions';
import type dayjs from 'dayjs';

import { REFERENCE_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { type PayerFeature } from 'services/FeatureFlags';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface PayerSegment {
    id: string;
    name: string;
    payerId: string;
}

export interface ConfiguredProductForPayer {
    oid: string;
    description: string;
    brandProductDescription: string;
    genericProductDescription: string;
    therapeuticCategory?: string;
}

export interface BiosimilarConversionProduct {
    value: string;
    is_brand: boolean;
    name: string;
    secondaryData: string;
    productServiceIds?: string[];
}

export interface ProductConfig {
    productServiceOid: string;
    hcpcsBillingLabel: string;
    ndcBillingLabel: string;
    multistrengthFlag: boolean;
    preferredBillingType: BenefitType;
}

export interface SupplierDetails {
    npi: string;
    name: string;
    phone?: string;
    fax?: string;
    email?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: USState;
    zip?: string;
    orgSize?: string;
    networkStatus: NetworkStatus;
    taxonomyCodes: { name: string; secondaryData: string }[];
    childNpis: string[];
    billingSupplierNpi?: string;
}

export interface PrescriberDetails {
    npi: string;
    name: string;
    phone?: string;
    fax?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    specialization?: string;
    practiceNpi?: string;
    organizationName?: string;
    pointOfContactFirstName?: string;
    pointOfContactLastName?: string;
    pointOfContactPhone?: string;
}

export interface DiagnosisCodeOption {
    description: string;
    code: string;
    name: string;
    secondaryData: string;
}

export interface ProductCodeOption {
    codesystem: string;
    code: string;
    oid: string;
    description: string;
    name: string;
    secondaryData: string;
    isBrand: boolean;
}

export const referenceApi = createApi({
    baseQuery: getBaseQuery(REFERENCE_API_URL),
    reducerPath: 'referenceApi',
    endpoints: (build) => ({
        getPayerSegmentsForSupplier: build.query<PayerSegment[], { supplierNpi: string }>({
            query: ({ supplierNpi }) => `payer_segments/forsupplier/${supplierNpi}`,
            transformResponse: (
                response: { segment_id: string; segment_name: string; payer_id: string }[],
            ) =>
                response
                    .map((segment) => ({
                        id: segment.segment_id,
                        name: segment.segment_name,
                        payerId: segment.payer_id,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: handleRTKQueryError(
                `An error occurred retrieving health plans for supplier.`,
            ),
        }),
        /** Gets list of payer segments */
        getPayerSegments: build.query<
            { payerSegmentList: PayerSegment[]; payerSegmentMap: Record<string, PayerSegment> },
            void
        >({
            query: () => 'payer_segments',
            transformResponse: (
                response: { segment_id: string; segment_name: string; payer_id: string }[],
            ) => {
                const payerSegmentList = response
                    .map((segment) => ({
                        id: segment.segment_id,
                        name: segment.segment_name,
                        payerId: segment.payer_id,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name));
                const payerSegmentMap = Object.fromEntries(
                    payerSegmentList.map((segment) => [segment.id, segment]),
                );
                return { payerSegmentList, payerSegmentMap };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving health plans.',
            ),
        }),
        getPayerDetails: build.query<
            {
                payerId: string;
                payerName: string;
                payerEmail?: string;
                payerFeatures?: PayerFeature[];
            },
            { payerId: string }
        >({
            query: ({ payerId }) => `payers/${payerId}`,
            transformResponse: (response: {
                payer_id: string;
                payer_name: string;
                payer_email?: string;
                features?: PayerFeature[];
            }) => ({
                payerId: response.payer_id,
                payerName: response.payer_name,
                payerEmail: response.payer_email,
                payerFeatures: response.features,
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving payer details.',
            ),
        }),
        getProductsMap: build.query<
            Record<
                string,
                { description: string; brandDescription: string; genericDescription: string }
            >,
            void
        >({
            query: () => '/productservices/summaryonly',
            transformResponse: (
                response: {
                    oid: string;
                    description: string;
                    brand_product_description: string;
                    generic_product_description: string;
                }[],
            ) =>
                Object.fromEntries(
                    (response ?? []).map((product) => [
                        product.oid,
                        {
                            description: product.description,
                            brandDescription: product.brand_product_description,
                            genericDescription: product.generic_product_description,
                        },
                    ]),
                ),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving products.'),
        }),
        getProductCodeArr: build.query<ProductCodeOption[], void>({
            query: () => 'productservices/?include_hcpcs_with_each_oid_under_parent=true',
            transformResponse: (
                response: {
                    oid: string;
                    intake?: boolean;
                    description?: string;
                    generic_product_description?: string;
                    brand_product_description?: string;
                    codes: {
                        codesystem: string;
                        code: string;
                        description: string;
                        is_brand: boolean;
                    }[];
                }[],
            ) =>
                (response ?? [])
                    .map((med) =>
                        med.codes.map((code) => ({
                            code: code.code,
                            description: code.description,
                            codesystem: code.codesystem,
                            oid: med.oid,
                            name: code.description,
                            secondaryData: code.code,
                            isBrand: code.is_brand,
                        })),
                    )
                    .flat()
                    .sort((a, b) => sortAsc(a.code, b.code)),
            transformErrorResponse: handleRTKQueryError('Error retrieving medication details.'),
        }),
        /** Gets list of configured products for a payer. */
        getConfiguredProductsForPayer: build.query<
            ConfiguredProductForPayer[],
            { segmentId: string }
        >({
            query: ({ segmentId }) => `product_benefit_configs/segments/${segmentId}`,
            transformResponse: (
                response: {
                    product_service_oid: string;
                    description: string;
                    brand_product_description: string;
                    generic_product_description: string;
                    therapeutic_category?: string;
                }[],
            ) =>
                response
                    .map((product) => ({
                        oid: product.product_service_oid,
                        description: product.description,
                        brandProductDescription: product.brand_product_description,
                        genericProductDescription: product.generic_product_description,
                        therapeuticCategory: product.therapeutic_category,
                    }))
                    .sort((a, b) => sortAsc(a.description, b.description)),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving configured products for health plan.',
            ),
        }),
        getProductConfigForPayer: build.query<
            ProductConfig[],
            { segmentId: string; productServiceOid: string }
        >({
            query: ({ segmentId, productServiceOid }) =>
                `product_benefit_configs/${segmentId}/${productServiceOid}`,
            transformResponse: (
                response: {
                    product_service_oid: string;
                    hcpcs_billing_label: string;
                    ndc_billing_label: string;
                    multistrength_flag: boolean;
                    preferred_billing_type: BenefitType;
                }[],
            ) =>
                response.map((config) => ({
                    hcpcsBillingLabel: config.hcpcs_billing_label,
                    multistrengthFlag: config.multistrength_flag,
                    ndcBillingLabel: config.ndc_billing_label,
                    preferredBillingType: config.preferred_billing_type,
                    productServiceOid: config.product_service_oid,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving product configuration for health plan.',
            ),
        }),
        getSupplierDetails: build.query<SupplierDetails, { npi: string }>({
            query: ({ npi }) => `suppliers/${npi}`,
            transformResponse: (response: {
                supplier_npi: string;
                organization_name: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: USState;
                postal_code?: string;
                telephone_number?: string;
                fax_number?: string;
                email?: string;
                org_size?: string;
                network_status: NetworkStatus;
                extra_taxonomy_codes: { taxonomy_code: string; taxonomy_description: string }[];
                children_npis?: string[];
                billing_supplier_npi?: string;
            }) => ({
                name: response?.organization_name,
                phone: response?.telephone_number,
                fax: response?.fax_number,
                email: response?.email,
                address1: response?.address_1,
                address2: response?.address_2,
                city: response?.city,
                state: response?.state,
                zip: response?.postal_code,
                npi: response?.supplier_npi,
                orgSize: response?.org_size,
                networkStatus: response?.network_status,
                taxonomyCodes:
                    response?.extra_taxonomy_codes?.map((code) => ({
                        name: code.taxonomy_code,
                        secondaryData: code.taxonomy_description,
                    })) ?? [],
                childNpis: response?.children_npis ?? [],
                billingSupplierNpi: response?.billing_supplier_npi,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving pharmacy details.', err);
                }
            },
        }),
        getPrescriberDetails: build.query<PrescriberDetails, { npi: string }>({
            query: ({ npi }) => `prescribers/${npi}`,
            transformResponse: (response: {
                prescriber_npi: string;
                first_name: string;
                last_name: string;
                organization_name?: string;
                practice_npi?: string;
                telephone_number?: string;
                fax_number?: string;
                address_1?: string;
                address_2?: string;
                city?: string;
                state?: string;
                postal_code?: string;
                specialization?: string;
                point_of_contact_first_name?: string;
                point_of_contact_last_name?: string;
                point_of_contact_phone?: string;
            }) => ({
                npi: response.prescriber_npi,
                name: formatNameLastFirst(response.last_name, response.first_name),
                phone: response.telephone_number,
                fax: response.fax_number,
                address1: response.address_1,
                address2: response.address_2,
                city: response.city,
                state: response.state,
                zip: response.postal_code,
                specialization: response.specialization,
                practiceNpi: response.practice_npi,
                organizationName: response.organization_name,
                pointOfContactFirstName: response.point_of_contact_first_name,
                pointOfContactLastName: response.point_of_contact_last_name,
                pointOfContactPhone: response.point_of_contact_phone,
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving prescriber details.', err);
                }
            },
        }),
        // gets diagnosis object
        getDiagnosis: build.query<
            {
                oid: string;
                description: string;
                codes: {
                    codesystem: string;
                    code: string;
                    description: string;
                    isBrand: boolean;
                }[];
            },
            { diagnosisOid: string }
        >({
            query: ({ diagnosisOid }) => `diagnoses/${diagnosisOid}`,
            transformResponse: (response: {
                oid: string;
                description: string;
                codes: {
                    codesystem: string;
                    code: string;
                    description: string;
                    is_brand: boolean;
                }[];
            }) => ({
                oid: response.oid,
                description: response.description,
                codes: response.codes.map((code) => ({
                    codesystem: code.codesystem,
                    code: code.code,
                    description: code.description,
                    isBrand: code.is_brand,
                })),
            }),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving diagnosis.'),
        }),
        getDiagnosesCodes: build.query<DiagnosisCodeOption[], void>({
            query: () => 'diagnoses/codes',
            transformResponse: (
                response: { codesystem: string; code: string; description: string }[],
            ) =>
                response?.map((code) => ({
                    description: code.description,
                    code: code.code,
                    // Used for selects
                    name: code.description,
                    secondaryData: code.code,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving diagnoses codes.',
            ),
        }),
        getPlaceOfServiceMap: build.query<Record<string, string>, void>({
            query: () => 'product_benefit_configs/places',
            transformResponse: (response: { code: string; description: string }[]) =>
                Object.fromEntries(
                    (response ?? []).map((option) => [option.code, option.description]),
                ),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving place of service options.',
            ),
        }),
        postClaimEstimateCost: build.query<
            { claim_fact: string; unit_price: string; qty: string },
            {
                productServiceOid: string;
                dateOfService: dayjs.ConfigType;
                referralStart: dayjs.ConfigType;
                priceSource: string;
                priceIsFrozen: boolean;
                qty: number;
                bidAmt: number;
                referentPriceType: ReferentPriceType;
                ndc?: string;
                hcpcsCode: string;
            }
        >({
            query: ({
                productServiceOid,
                dateOfService,
                referralStart,
                priceSource,
                priceIsFrozen,
                qty,
                bidAmt,
                referentPriceType,
                ndc,
                hcpcsCode,
            }) => ({
                url: 'prices/medical_claim_fact',
                method: 'POST',
                body: {
                    product_service_oid: productServiceOid,
                    date_of_service: formatDate(dateOfService, DateFormats.HYPHEN_DATE),
                    referral_start: formatDate(referralStart, DateFormats.HYPHEN_DATE),
                    price_source: priceSource,
                    price_is_frozen: priceIsFrozen,
                    qty,
                    bid_amt: bidAmt,
                    referent_price_type: referentPriceType,
                    ndc,
                    hcpcs_code: hcpcsCode,
                },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred calculating estimated provider charge.',
            ),
        }),
        getEligiblePricesForOid: build.query<
            { code: string; price: string }[],
            {
                oid: string;
                referentPriceType: ReferentPriceType;
                isBrand: boolean;
                priceSource: string;
                priceDate?: dayjs.ConfigType;
                benefitType?: BenefitType;
            }
        >({
            query: ({ oid, referentPriceType, isBrand, priceSource, priceDate, benefitType }) => ({
                url: `prices/oid/${oid}/eligible_prices`,
                params: {
                    referent_price_type: referentPriceType,
                    is_brand: isBrand,
                    price_source: priceSource,
                    price_date: formatDate(priceDate, DateFormats.HYPHEN_DATE),
                    benefit_type: benefitType,
                },
            }),
            transformResponse: (response: { code: string; price: string }[]) =>
                response?.map((price) => ({ code: price.code, price: price.price })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving unit price info.',
            ),
        }),
        putLddCapabilities: build.mutation<
            void,
            {
                pharmacyNpi: string;
                added_capabilities: { product_service_oid: string; drug_version: number }[];
                removed_capabilities: { product_service_oid: string; drug_version: number }[];
            }
        >({
            query: ({ pharmacyNpi, added_capabilities, removed_capabilities }) => ({
                url: `capabilities/${pharmacyNpi}/drug`,
                method: 'PUT',
                body: { added_capabilities, removed_capabilities },
            }),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred updating LDD capabilities.',
            ),
        }),
        getBiosimilarConversionProducts: build.query<
            BiosimilarConversionProduct[],
            {
                productServiceOid: string;
                payerId: string;
                segmentId: string;
                memberState: string;
                benefit: string;
                placeOfService?: string;
                lineOfBusiness?: string;
                lobSegment?: string;
            }
        >({
            query: ({
                productServiceOid,
                payerId,
                segmentId,
                memberState,
                benefit,
                placeOfService,
                lineOfBusiness,
                lobSegment,
            }) => ({
                url: `/therapy_groups/by_product_service_oid/${productServiceOid}/transition_products_for_npi`,
                params: {
                    payer_id: payerId,
                    segment_id: segmentId,
                    member_state: memberState,
                    benefit,
                    place_of_service: placeOfService,
                    line_of_business: lineOfBusiness,
                    lob_segment: lobSegment,
                },
            }),
            transformResponse: (
                response: {
                    product_service_oid: string;
                    is_brand: boolean;
                    brand_product_description?: string;
                    description?: string;
                    product_service_ids?: string[];
                }[],
            ) =>
                (response ?? [])
                    .map((product) => ({
                        value: product.product_service_oid,
                        is_brand: product.is_brand,
                        name: product.is_brand
                            ? (product.brand_product_description ?? '')
                            : (product.description ?? ''),
                        secondaryData: product.is_brand ? 'Brand' : 'Generic',
                        productServiceIds: product.product_service_ids,
                    }))
                    .sort((a, b) => sortAsc(a.name, b.name)),
            transformErrorResponse: (err) => {
                const isMissingTherapyGroup =
                    err.status === 404 &&
                    (err.data as { detail?: string } | undefined)?.detail ===
                        'No therapy group found for this product service oid.';

                if (!isMissingTherapyGroup) {
                    handleError(
                        'An error occurred retrieving biosimilar conversion products.',
                        err,
                    );
                }
            },
        }),
    }),
});

export const {
    useGetPayerSegmentsForSupplierQuery,
    useGetProductsMapQuery,
    useGetProductCodeArrQuery,
    useGetProductConfigForPayerQuery,
    useGetConfiguredProductsForPayerQuery,
    useGetSupplierDetailsQuery,
    useGetPrescriberDetailsQuery,
    useGetDiagnosesCodesQuery,
    useGetPlaceOfServiceMapQuery,
    usePostClaimEstimateCostQuery,
    useGetEligiblePricesForOidQuery,
    useGetPayerSegmentsQuery,
    useGetPayerDetailsQuery,
    useGetDiagnosisQuery,
    usePutLddCapabilitiesMutation,
    useGetBiosimilarConversionProductsQuery,
} = referenceApi;
